import React, { FC } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";

import { isChrome, isSafari, isFirefox, isEdge, isMobile } from "react-device-detect";

import { NewsContextProvider } from "./context/NewsContext/NewsContext";
import { ImagesProvider } from "./context/ImagesProvider";
import { EventsContextProvider } from "./context/EventsContext/NewsContext";
import { DiscoveryContextProvider } from "./context/DiscoveryContext/DiscoveryContext";
import { CDNFileContextProvider } from "./context/CDNFileContext/CDNFileContext";
import { DocCategoryContextProvider } from "./context/DocumentCategoryContext/DocumentCategoryContext";
import { MobileAppVersionContextProvider } from "./context/MobileAppVersionContext/MobileAppVersionContext";
import { BannerContextProvider } from "./context/BannerContext/BannerContext";
import { BannerMobileContextProvider } from "./context/BannerMobileContext/BannerMobileContext";
import { DocumentContextProvider } from "./context/DocumentContext/DocumentContext";

import MainHeader from "./components/HeaderComponent";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/Routing";
import Login from "./components/Routing/Login";
import Sidebar from "./components/SidebarComponent";

import HomePage from "./pages/HomePage";
import AuthWarning from "./pages/Auth/AuthWarning";
import Logout from "./pages/Logout";

import MainContainer from "./App.style";
import { SidebarMenuProvider } from "./context/SidebarMenuProvider";
import useScreenWidth from "./utils/useScreenWidth";
import Warning from "./pages/Auth/Warning";
import MobileWarning from "./pages/Auth/MobileWarning";

import AddNews from "./pages/AddNews";
import AddEvents from "./pages/AddEvents";
import EditNews from "./pages/EditNews";

import EditEvents from "./pages/EditEvents";
import NewsList from "./pages/NewsList";
import EventList from "./pages/EventList";

import DiscoveryList from "./pages/DiscoveryList";
import AddDiscovery from "./pages/AddDiscovery";
import EditDiscovery from "./pages/EditDiscovery";

import CDNFileList from "./pages/CDNFileList";
import AddCDNFile from "./pages/AddCDNFile";
import EditCDNFile from "./pages/EditCDNFile";

import DocumentCategoryList from "./pages/DocumentCategoryList";
import AddDocumentCategory from "./pages/AddDocumentCategory";

import MobileAppVersionList from "./pages/MobileAppVersionList";
import AddMobileAppVersion from "./pages/AddMobileAppVersion";
import EditMobileAppVersion from "./pages/EditMobileAppVersion";

import BannerList from "./pages/BannerList";
import AddBanner from "./pages/AddBanner";
import EditBanner from "./pages/EditBanner";
import OrderBanner from "./pages/OrderBanner";

import BannerMobileList from "./pages/BannerMobileList";
import AddBannerMobile from "./pages/AddBannerMobile";
import EditBannerMobile from "./pages/EditBannerMobile";
import OrderBannerMobile from "./pages/OrderBannerMobile";

import DocumentList from "./pages/DocumentList";
import AddDocument from "./pages/AddDocument";
import EditDocument from "./pages/EditDocument";

interface LayoutContainerProps {
	children: React.ReactNode;
}

const WIDTH_MESSAGE = "ScreenWidthMessage";
const BROWSER_MESSAGE = "ScreenBrowserMessage";
const MOBILE_MESSAGE = "ScreenMobileMessage";

const ProtectedLayout: FC<LayoutContainerProps> = ({ children }) => {
	return (
		<ImagesProvider>
			<SidebarMenuProvider>
				<Layout>
					<MainHeader />
					<MainContainer>
						<Sidebar />
						{children}
					</MainContainer>
					<Footer />
				</Layout>
			</SidebarMenuProvider>
		</ImagesProvider>
	);
};

const App: FC = () => {
	const isScreenWidthSmall = useScreenWidth();

	if (isMobile) {
		return <MobileWarning queryString={MOBILE_MESSAGE} />;
	} else if (isChrome || isSafari || isFirefox || isEdge) {
		if (isScreenWidthSmall) {
			return <Warning queryString={WIDTH_MESSAGE} />;
		}
	} else {
		return <Warning queryString={BROWSER_MESSAGE} />;
	}


	return (
		<ConfigProvider
			theme={{
				token: {
					fontFamily: "Roboto, sans-serif",
					lineHeight: 1.38,
				},
			}}
		>
			<Router>
				<NewsContextProvider>
					<DiscoveryContextProvider>
						<EventsContextProvider>
							<CDNFileContextProvider>
								<DocCategoryContextProvider>
									<DocumentContextProvider>
										<MobileAppVersionContextProvider>
											<BannerContextProvider>
												<BannerMobileContextProvider>
													<Routes>
														<Route element={<ProtectedRoute />}>
															<Route
																path="/"
																element={(
																	<ProtectedLayout>
																		<HomePage />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/news"
																element={(
																	<ProtectedLayout>
																		<NewsList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-news"
																element={(
																	<ProtectedLayout>
																		<AddNews />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-news/:id"
																element={(
																	<ProtectedLayout>
																		<EditNews />
																	</ProtectedLayout>
																)}
															/>

															<Route
																path="/events"
																element={(
																	<ProtectedLayout>
																		<EventList />
																	</ProtectedLayout>
																)}
															/> 
															<Route
																path="/add-events"
																element={(
																	<ProtectedLayout>
																		<AddEvents />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-events/:id"
																element={(
																	<ProtectedLayout>
																		<EditEvents />
																	</ProtectedLayout>
																)}
															/>

															<Route
																path="/discovery"
																element={(
																	<ProtectedLayout>
																		<DiscoveryList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-discovery"
																element={(
																	<ProtectedLayout>
																		<AddDiscovery />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-discovery/:id"
																element={(
																	<ProtectedLayout>
																		<EditDiscovery />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/cdn"
																element={(
																	<ProtectedLayout>
																		<CDNFileList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-cdn-file"
																element={(
																	<ProtectedLayout>
																		<AddCDNFile />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-cdn-file/:id"
																element={(
																	<ProtectedLayout>
																		<EditCDNFile />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/doc-categories"
																element={(
																	<ProtectedLayout>
																		<DocumentCategoryList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-doc-category"
																element={(
																	<ProtectedLayout>
																		<AddDocumentCategory />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/documents/:id"
																element={(
																	<ProtectedLayout>
																		<DocumentList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-document/:id"
																element={(
																	<ProtectedLayout>
																		<AddDocument />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-document/:id"
																element={(
																	<ProtectedLayout>
																		<EditDocument />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/mobile-app-versions"
																element={(
																	<ProtectedLayout>
																		<MobileAppVersionList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-mobile-app-version"
																element={(
																	<ProtectedLayout>
																		<AddMobileAppVersion />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-mobile-app-version/:id"
																element={(
																	<ProtectedLayout>
																		<EditMobileAppVersion />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/banners"
																element={(
																	<ProtectedLayout>
																		<BannerList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-banner"
																element={(
																	<ProtectedLayout>
																		<AddBanner />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-banner/:id"
																element={(
																	<ProtectedLayout>
																		<EditBanner />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/order-banners"
																element={(
																	<ProtectedLayout>
																		<OrderBanner />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/mobile-banners"
																element={(
																	<ProtectedLayout>
																		<BannerMobileList />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/add-mobile-banner"
																element={(
																	<ProtectedLayout>
																		<AddBannerMobile />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/edit-mobile-banner/:id"
																element={(
																	<ProtectedLayout>
																		<EditBannerMobile />
																	</ProtectedLayout>
																)}
															/>
															<Route
																path="/order-mobile-banners"
																element={(
																	<ProtectedLayout>
																		<OrderBannerMobile />
																	</ProtectedLayout>
																)}
															/>
														</Route>
														<Route path="/auth-warning" element={<AuthWarning />} />
														<Route path="/accounts/login" element={<Login />} />
														<Route path="/accounts/logout" element={<Logout />} />
													</Routes>
												</BannerMobileContextProvider>
											</BannerContextProvider>
										</MobileAppVersionContextProvider>
									</DocumentContextProvider>
								</DocCategoryContextProvider>
							</CDNFileContextProvider>
						</EventsContextProvider>
					</DiscoveryContextProvider>
				</NewsContextProvider>
			</Router>
		</ConfigProvider>
	);
};

export default App;
